import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { useBibtexImportStore } from "@/stores/bibtex-import-store";

interface BibtexUploadResponse {
  job_id: string;
  entries_found: number;
  entries_with_doi: number;
  entries_without_doi: number;
  parse_errors: string[];
}

export function useBibtexImport(conceptId: string) {
  const queryClient = useQueryClient();

  const uploadAndImport = useCallback(
    async (file: File, role: "foundational" | "trawled" = "trawled") => {
      const { startImport, updateProgress, completeImport, failImport } =
        useBibtexImportStore.getState();

      try {
        // Build FormData — do NOT set Content-Type; ky/fetch auto-sets multipart boundary
        const formData = new FormData();
        formData.append("file", file);

        // POST to start import job
        const res = await apiClient
          .post(`api/concepts/${conceptId}/import/bibtex?role=${role}`, {
            body: formData,
            timeout: 120_000,
          })
          .json<BibtexUploadResponse>();

        // Show parse errors if any
        if (res.parse_errors.length > 0) {
          toast.warning("BibTeX parse warnings", {
            description: `${res.parse_errors.length} entries had issues: ${res.parse_errors.slice(0, 3).join("; ")}`,
          });
        }

        // Update store with real job info
        startImport(res.job_id, res.entries_found);

        toast.info("BibTeX import started", {
          description: `Resolving ${res.entries_found} entries (${res.entries_with_doi} with DOI)`,
        });

        // Open SSE stream
        const baseUrl =
          import.meta.env.VITE_API_URL ?? "http://localhost:8000";
        const token = await getAuthToken();
        const streamUrl = `${baseUrl}/api/concepts/${conceptId}/import/bibtex/${res.job_id}/events`;

        const controller = new AbortController();
        let receivedTerminal = false;

        for await (const event of streamSSE(
          streamUrl,
          { Authorization: `Bearer ${token}` },
          controller.signal,
        )) {
          if (event.event === "progress") {
            const data = JSON.parse(event.data);
            updateProgress({
              resolved: data.resolved,
              total: data.total,
              statusMessage: `Resolving ${data.resolved} of ${data.total} entries`,
            });
          } else if (event.event === "complete") {
            const data = JSON.parse(event.data);
            const msg = `Added ${data.added}, ${data.duplicates} duplicates, ${data.merged} merged`;
            receivedTerminal = true;
            // Await the papers refetch BEFORE flipping isCompleted:
            // curate.tsx's auto-score effect reads the ["papers"] cache
            // synchronously on the false->true edge of isCompleted, so the
            // imported papers must be in cache first (mirrors useSmartTrawl).
            try {
              await queryClient.invalidateQueries({
                queryKey: ["papers", conceptId],
              });
            } catch {
              // Refetch failed — still complete; papers are added, so don't
              // route to failImport(). Auto-score may use a stale cache.
            }
            completeImport(msg);
            toast.success("BibTeX import complete", { description: msg });
            return;
          } else if (event.event === "error") {
            const data = JSON.parse(event.data);
            const errMsg = data.error || "Import failed";
            failImport(errMsg);
            toast.error("BibTeX import failed", { description: errMsg });
            receivedTerminal = true;
            return;
          }
        }

        // Stream closed without terminal event — refresh table as fallback
        if (!receivedTerminal) {
          try {
            await queryClient.invalidateQueries({
              queryKey: ["papers", conceptId],
            });
          } catch {
            // Refetch failed — still complete; auto-score may use stale cache.
          }
          completeImport("Import finished");
          toast.success("BibTeX import complete");
        }
      } catch (err) {
        const { failImport } = useBibtexImportStore.getState();
        const msg = err instanceof Error ? err.message : "BibTeX import failed";
        failImport(msg);
        toast.error("BibTeX import error", { description: msg });
      }
    },
    [conceptId, queryClient],
  );

  return { uploadAndImport };
}
